import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";

const LOADING_GIF = "/assets/images/globegif.gif";

// Shows the loading gif in place of an image until the real image
// has actually finished fetching, then swaps to it. Same as the
// LazyImg used in HomeScreen.jsx, kept consistent across components.
const LazyImg = ({ src, alt, className = "", ...rest }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!src) return undefined;
    setLoaded(false);
    let cancelled = false;
    const preloader = new window.Image();
    preloader.src = src;
    preloader.onload = () => {
      if (!cancelled) setLoaded(true);
    };
    preloader.onerror = () => {
      if (!cancelled) setLoaded(true); // fall through so it doesn't stay stuck on gif forever
    };
    return () => {
      cancelled = true;
    };
  }, [src]);

  return (
    <img
      src={loaded ? src : LOADING_GIF}
      alt={alt}
      className={`${className} ${!loaded ? "lazy-img-loading" : ""}`.trim()}
      loading="lazy"
      {...rest}
    />
  );
};

// ══════════════════════════════════════════════
// STEP 2 MODAL — Package Detail
// ══════════════════════════════════════════════
function PackageDetailModal({ pkg, tourName, onBack, onClose }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={overlayStyle}
    >
      <div style={modalBoxStyle}>
        {/* Top buttons */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px 0" }}>
          <button onClick={onBack} style={backBtnStyle}>
            ← Back to Packages
          </button>
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
        </div>

        {/* Gradient header */}
        <div style={{ background: "linear-gradient(135deg,#0f5e6e,#00b4d8)", padding: "1.5rem 2rem", color: "#fff" }}>
          <p style={{ fontSize: "0.82rem", opacity: 0.8, marginBottom: 4 }}>{tourName}</p>
          <h2 style={{ fontSize: "1.5rem", fontWeight: 800, margin: "0 0 12px" }}>{pkg.packageName}</h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
            {pkg.destination && <span style={chipWhite}>📍 {pkg.destination}</span>}
            {pkg.duration && <span style={chipWhite}>🕐 {pkg.duration}</span>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {pkg.discountPrice ? (
              <>
                <span style={{ fontSize: "1.9rem", fontWeight: 900 }}>
                  ₹{Number(pkg.discountPrice).toLocaleString("en-IN")}
                </span>
                <span style={{ fontSize: "1rem", textDecoration: "line-through", opacity: 0.65 }}>
                  ₹{Number(pkg.price).toLocaleString("en-IN")}
                </span>
                <span style={{ background: "rgba(255,255,255,0.25)", padding: "3px 10px", borderRadius: 20, fontSize: "0.8rem", fontWeight: 700 }}>
                  Save ₹{(Number(pkg.price) - Number(pkg.discountPrice)).toLocaleString("en-IN")}
                </span>
              </>
            ) : (
              <span style={{ fontSize: "1.9rem", fontWeight: 900 }}>
                {pkg.price ? `₹${Number(pkg.price).toLocaleString("en-IN")}` : "Price on request"}
              </span>
            )}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "1.5rem 2rem 2rem", overflowY: "auto" }}>
          {pkg.description && (
            <div style={{ marginBottom: "1.2rem" }}>
              <h4 style={sectionTitle}>📋 Overview</h4>
              <p style={bodyText}>{pkg.description}</p>
            </div>
          )}
          {pkg.itinerary && (
            <div style={{ marginBottom: "1.2rem" }}>
              <h4 style={sectionTitle}>🗓️ Itinerary</h4>
              <p style={{ ...bodyText, whiteSpace: "pre-wrap" }}>{pkg.itinerary}</p>
            </div>
          )}
          {(pkg.inclusions || pkg.exclusions) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1.2rem" }}>
              {pkg.inclusions && (
                <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 14, padding: "1rem" }}>
                  <h4 style={{ color: "#166534", fontWeight: 700, marginBottom: 8, fontSize: "0.9rem" }}>✅ Inclusions</h4>
                  <p style={{ ...bodyText, whiteSpace: "pre-wrap" }}>{pkg.inclusions}</p>
                </div>
              )}
              {pkg.exclusions && (
                <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 14, padding: "1rem" }}>
                  <h4 style={{ color: "#991b1b", fontWeight: 700, marginBottom: 8, fontSize: "0.9rem" }}>❌ Exclusions</h4>
                  <p style={{ ...bodyText, whiteSpace: "pre-wrap" }}>{pkg.exclusions}</p>
                </div>
              )}
            </div>
          )}

          {/* WhatsApp Enquire */}
          <button
            onClick={() => window.open(
              `https://wa.me/919096785868?text=Hi! I'm interested in the "${pkg.packageName}" package from "${tourName}" tour.`,
              "_blank"
            )}
            style={whatsappBtnStyle}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.02)"; e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.2)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
          >
            <img src="/assets/images/whatsapp.png" alt="WhatsApp" style={{ width: 22, height: 22, objectFit: "contain" }} />
            Enquire on WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════
// STEP 1 MODAL — All Packages of a Tour
// ══════════════════════════════════════════════
function TourPackagesModal({ tour, onClose, onSelectPackage }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const packages = tour.packages || [];

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={overlayStyle}
    >
      <div style={{ ...modalBoxStyle, maxWidth: 750 }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1.2rem 1.5rem", borderBottom: "1px solid #e2e8f0" }}>
          <div>
            <p style={{ fontSize: "0.8rem", color: "#00b4d8", fontWeight: 600, marginBottom: 2 }}>Available Packages</p>
            <h3 style={{ fontSize: "1.25rem", fontWeight: 800, color: "#1a2a3f", margin: 0 }}>{tour.tourName}</h3>
          </div>
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
        </div>

        {/* Tour image + description */}
        {(tour.imageUrls?.[0] || tour.description) && (
          <div style={{ display: "flex", gap: "1rem", padding: "1rem 1.5rem", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", alignItems: "center" }}>
            {tour.imageUrls?.[0] && (
              <LazyImg
                src={tour.imageUrls[0]}
                alt={tour.tourName}
                style={{ width: 90, height: 70, objectFit: "cover", borderRadius: 12, flexShrink: 0 }}
              />
            )}
            {tour.description && (
              <p style={{ fontSize: "0.88rem", color: "#64748b", margin: 0, lineHeight: 1.5 }}>
                {tour.description.length > 180 ? tour.description.slice(0, 180) + "…" : tour.description}
              </p>
            )}
          </div>
        )}

        {/* Packages list */}
        <div style={{ padding: "1rem 1.5rem 1.5rem", overflowY: "auto", maxHeight: "55vh" }}>
          {packages.length === 0 ? (
            <p style={{ textAlign: "center", color: "#94a3b8", padding: "2rem" }}>
              No packages available for this tour right now.
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  onClick={() => onSelectPackage(pkg)}
                  className="tour-pkg-item" /* 👈 YE CLASS ADD KI HAI */
                  style={{
                    border: "1.5px solid #e2e8f0",
                    borderRadius: 18,
                    padding: "1rem 1.2rem",
                    cursor: "pointer",
                    transition: "all 0.25s",
                    background: "#fff",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#00b4d8";
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,180,216,0.12)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.transform = "none";
                  }}
                >
                  {/* Left: info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h4 style={{ fontSize: "1rem", fontWeight: 700, color: "#1a2a3f", margin: "0 0 6px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {pkg.packageName}
                    </h4>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                      {pkg.destination && <span style={chipTeal}>📍 {pkg.destination}</span>}
                      {pkg.duration && <span style={chipTeal}>🕐 {pkg.duration}</span>}
                    </div>
                    {pkg.description && (
                      <p style={{ fontSize: "0.82rem", color: "#64748b", margin: 0, lineHeight: 1.4 }}>
                        {pkg.description.length > 80 ? pkg.description.slice(0, 80) + "…" : pkg.description}
                      </p>
                    )}
                    {pkg.inclusions && (
                      <p style={{ fontSize: "0.78rem", color: "#16a34a", margin: "4px 0 0", fontWeight: 600 }}>
                        ✅ {pkg.inclusions.length > 60 ? pkg.inclusions.slice(0, 60) + "…" : pkg.inclusions}
                      </p>
                    )}
                  </div>

                  {/* Right: price + button */}
                  <div className="tour-pkg-right" style={{ flexShrink: 0, textAlign: "right" }}> {/* 👈 YE CLASS ADD KI HAI */}
                    {pkg.discountPrice ? (
                      <>
                        <div style={{ fontSize: "1.3rem", fontWeight: 900, color: "#0f5e6e" }}>
                          ₹{Number(pkg.discountPrice).toLocaleString("en-IN")}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "#94a3b8", textDecoration: "line-through" }}>
                          ₹{Number(pkg.price).toLocaleString("en-IN")}
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: "1.2rem", fontWeight: 900, color: "#0f5e6e" }}>
                        {pkg.price ? `₹${Number(pkg.price).toLocaleString("en-IN")}` : "On request"}
                      </div>
                    )}
                    <button
                      style={{
                        marginTop: 8,
                        background: "linear-gradient(135deg,#0f5e6e,#00b4d8)",
                        color: "#fff", border: "none", padding: "7px 16px",
                        borderRadius: 50, fontWeight: 700, fontSize: "0.8rem",
                        cursor: "pointer", whiteSpace: "nowrap",
                      }}
                    >
                      View Details →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom WhatsApp */}
        <div style={{ padding: "0 1.5rem 1.5rem" }}>
          <button
            onClick={() => window.open(
              `https://wa.me/919096785868?text=Hi! I'm interested in the "${tour.tourName}" tour. Can you help me choose a package?`,
              "_blank"
            )}
            style={{ ...whatsappBtnStyle, fontSize: "0.95rem", padding: "12px 20px" }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.02)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; }}
          >
            <img src="/assets/images/whatsapp.png" alt="WhatsApp" style={{ width: 20, height: 20, objectFit: "contain" }} />
            Need help choosing? Chat on WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════
// SHARED STYLES
// ══════════════════════════════════════════════
const overlayStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
  zIndex: 9999, display: "flex", alignItems: "center",
  justifyContent: "center", padding: "1rem", backdropFilter: "blur(4px)",
};
const modalBoxStyle = {
  background: "#fff", borderRadius: 24, width: "100%",
  maxWidth: 680, maxHeight: "92vh", overflow: "hidden",
  boxShadow: "0 30px 60px rgba(0,0,0,0.25)",
  display: "flex", flexDirection: "column",
};
const closeBtnStyle = {
  background: "rgba(0,0,0,0.08)", color: "#1a2a3f", border: "none",
  borderRadius: "50%", width: 34, height: 34, fontSize: "1rem",
  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
  flexShrink: 0,
};
const backBtnStyle = {
  background: "none", border: "none", color: "#00b4d8",
  fontWeight: 700, fontSize: "0.88rem", cursor: "pointer", padding: 0,
};
const chipWhite = {
  background: "rgba(255,255,255,0.2)", color: "#fff",
  padding: "3px 10px", borderRadius: 20, fontSize: "0.78rem", fontWeight: 600,
};
const chipTeal = {
  fontSize: "0.75rem", background: "#e0f5fb", color: "#0f5e6e",
  padding: "3px 10px", borderRadius: 20, fontWeight: 600,
};
const sectionTitle = {
  fontSize: "1rem", fontWeight: 700, color: "#0f5e6e",
  marginBottom: 8, borderLeft: "3px solid #00b4d8", paddingLeft: 10,
};
const bodyText = { color: "#475569", lineHeight: 1.7, fontSize: "0.92rem", margin: 0 };
const whatsappBtnStyle = {
  width: "100%", background: "linear-gradient(115deg,#075e54,#128c7e)",
  color: "#fff", border: "none", padding: "13px 24px",
  borderRadius: 60, fontWeight: 800, fontSize: "1rem",
  cursor: "pointer", display: "flex", alignItems: "center",
  justifyContent: "center", gap: 10, transition: "all 0.3s",
};

// ══════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════
const ToursPackages = ({ previewMode = false }) => {
  const navigate = useNavigate();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state: step 1 = packages list, step 2 = single package detail
  const [activeTour, setActiveTour] = useState(null);       // step 1
  const [activePackage, setActivePackage] = useState(null); // step 2

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const toursQ = previewMode
          ? query(collection(db, "tours"), where("isActive", "==", true), orderBy("createdAt", "desc"), limit(4))
          : query(collection(db, "tours"), where("isActive", "==", true), orderBy("createdAt", "desc"));

        const toursSnap = await getDocs(toursQ);

        const toursWithPkgs = await Promise.all(
          toursSnap.docs.map(async (tourDoc) => {
            const tour = { id: tourDoc.id, ...tourDoc.data() };
            const pkgsQ = query(
              collection(db, "tours", tourDoc.id, "packages"),
              where("isActive", "==", true),
              orderBy("createdAt", "desc")
            );
            const pkgsSnap = await getDocs(pkgsQ);
            tour.packages = pkgsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            return tour;
          })
        );

        setTours(toursWithPkgs);
      } catch (err) {
        console.error("Error fetching tours:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTours();
  }, [previewMode]);

  const handleCloseAll = () => {
    setActiveTour(null);
    setActivePackage(null);
  };

  const handleOpenTour = (tour) => {
    setActivePackage(null);
    setActiveTour(tour);
  };

  const handleSelectPackage = (pkg) => {
    setActivePackage(pkg);
  };

  const handleBackToPackages = () => {
    setActivePackage(null);
  };

  if (loading) {
    return (
      <section className="packages-section">
        <p style={{ textAlign: "center", color: "#64748b", padding: "2rem" }}>Loading packages...</p>
      </section>
    );
  }

  if (tours.length === 0) {
    return (
      <section className="packages-section">
        <p style={{ textAlign: "center", color: "#64748b", padding: "2rem" }}>No packages available.</p>
      </section>
    );
  }

  return (
    <>
      <section className="packages-section">
        {previewMode ? (
          <div className="section-header">
            <div>
              <p className="section-eyebrow">🧳 Handpicked Trips</p>
              <h2 className="section-title">Featured Tour Packages</h2>
            </div>
            <button className="section-viewall-btn" onClick={() => navigate("/tours")}>
              View All Packages <i className="fas fa-arrow-right"></i>
            </button>
          </div>
        ) : (
          <h2>Featured Tour Packages</h2>
        )}

        <div className="packages-grid">
          {tours.map((tour) => {
            const img = tour.imageUrls?.[0];
            const totalPkgs = tour.packages?.length || 0;
            // Starting price from cheapest package
            const prices = (tour.packages || [])
              .map((p) => Number(p.discountPrice || p.price || 0))
              .filter((n) => n > 0);
            const startingPrice = prices.length > 0 ? Math.min(...prices) : null;

            return (
              <div className="package-card" key={tour.id} style={{ cursor: "pointer" }} onClick={() => handleOpenTour(tour)}>
                {img ? (
                  <LazyImg src={img} alt={tour.tourName} />
                ) : (
                  <div style={{
                    height: 240, background: "linear-gradient(135deg,#cfe8f3,#a8d8ea)",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "3rem",
                  }}>🗺️</div>
                )}

                <div className="card-content">
                  <h3>{tour.tourName}</h3>

                  {tour.description && (
                    <p style={{ color: "#718096", fontSize: "0.88rem", marginBottom: "0.5rem" }}>
                      {tour.description.length > 80 ? tour.description.slice(0, 80) + "…" : tour.description}
                    </p>
                  )}

                  {/* Package count */}
                  <p style={{ fontSize: "0.85rem", color: "#00b4d8", fontWeight: 700, margin: "6px 0" }}>
                    🧳 {totalPkgs > 0 ? `${totalPkgs} Package${totalPkgs > 1 ? "s" : ""} available` : "No packages yet"}
                  </p>

                  {/* Starting price */}
                  {startingPrice ? (
                    <div style={{ margin: "4px 0 12px" }}>
                      <span style={{ fontSize: "0.78rem", color: "#94a3b8" }}>Starting from </span>
                      <span className="price" style={{ fontSize: "1.2rem" }}>₹{startingPrice.toLocaleString("en-IN")}</span>
                    </div>
                  ) : (
                    <p className="price" style={{ fontSize: "1rem", marginBottom: 12 }}>Price on request</p>
                  )}

                  {/* View Details button */}
                  <button
                    className="card-btn"
                    onClick={(e) => { e.stopPropagation(); handleOpenTour(tour); }}
                  >
                    View Details &amp; Packages →
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* WhatsApp CTA */}
        <div style={{
          marginTop: "2.5rem",
          background: "linear-gradient(115deg,#075e54,#128c7e)",
          borderRadius: 32, padding: "2rem", textAlign: "center", color: "#fff",
        }}>
          <p style={{ fontSize: "1.1rem", fontWeight: 700, marginBottom: 6 }}>
            🤔 Can't decide which package suits you?
          </p>
          <p style={{ opacity: 0.9, marginBottom: "1rem", fontSize: "0.95rem" }}>
            Chat with our travel experts for a free personalized recommendation!
          </p>
          <button
            onClick={() => window.open("https://wa.me/919096785868?text=Hi! I need help choosing a tour package.", "_blank")}
            style={{
              background: "#fff", border: "none", padding: "12px 28px",
              borderRadius: 60, fontWeight: 800, color: "#075e54",
              fontSize: "1rem", cursor: "pointer", transition: "all 0.3s",
              display: "inline-flex", alignItems: "center", gap: 10,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.05)"; e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.2)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
          >
            <img src="/assets/images/whatsapp.png" alt="WhatsApp" style={{ width: 24, height: 24, objectFit: "contain" }} />
            Chat on WhatsApp
          </button>
        </div>

        {previewMode && (
          <div className="section-bottom-viewall">
            <button className="section-viewall-bottom-btn" onClick={() => navigate("/tours")}>
              <i className="fas fa-suitcase-rolling"></i> Explore All Packages
            </button>
          </div>
        )}
      </section>

      {/* STEP 1: Tour packages list modal */}
      {activeTour && !activePackage && (
        <TourPackagesModal
          tour={activeTour}
          onClose={handleCloseAll}
          onSelectPackage={handleSelectPackage}
        />
      )}

      {/* STEP 2: Single package detail modal */}
      {activeTour && activePackage && (
        <PackageDetailModal
          pkg={activePackage}
          tourName={activeTour.tourName}
          onBack={handleBackToPackages}
          onClose={handleCloseAll}
        />
      )}
    </>
  );
};

export default ToursPackages;